import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Search,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Mail,
  Eye,
  Loader,
  Trash2,
} from 'lucide-react';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

// ─── 类型 ─────────────────────────────────────────────────────────────────────

interface WpCustomer {
  id: number;
  username: string;
  name: string;
  email: string;
  registered_date: string;
  roles: string[];
  booking_count: number;
}

interface BookingRecord {
  id: number;
  date: string;
  status: string;
  customer_name: string;
  customer_email: string;
  total_price: number;
  created_at: string;
  start_time: string;
  end_time: string;
  court_name: string;
  location_name: string;
}

interface CustomerDetail {
  user: WpCustomer;
  bookings: BookingRecord[];
  pagination: { current_page: number; per_page: number; total: number; total_pages: number };
}

interface CreateForm {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
}

const STATUS_LABEL_KEYS: Record<string, { key: string; className: string }> = {
  confirmed: { key: 'customerManagement.statusConfirmed', className: 'badge-success' },
  pending: { key: 'customerManagement.statusPending', className: 'badge-warning' },
  cancelled: { key: 'customerManagement.statusCancelled', className: 'badge-danger' },
  completed: { key: 'customerManagement.statusCompleted', className: 'badge-info' },
};

// ─── 快速注册 Dialog ──────────────────────────────────────────────────────────

interface CreateDialogProps {
  onClose: () => void;
  onCreated: () => void;
}

const CreateCustomerDialog: React.FC<CreateDialogProps> = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateForm>({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<CreateForm>>({});

  const validate = () => {
    const e: Partial<CreateForm> = {};
    if (!form.username.trim()) e.username = t('customerManagement.errorUsernameRequired');
    if (!form.email.trim()) e.email = t('customerManagement.errorEmailRequired');
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = t('customerManagement.errorEmailInvalid');
    if (!form.password) e.password = t('customerManagement.errorPasswordRequired');
    else if (form.password.length < 8) e.password = t('customerManagement.errorPasswordLength');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await api.wpCustomers.create({
        username: form.username,
        email: form.email,
        password: form.password,
        first_name: form.first_name || undefined,
        last_name: form.last_name || undefined,
        phone: form.phone || undefined,
      });

      if (res.error) {
        const wpCode = (res as { wp_code?: string }).wp_code;
        if (wpCode === 'existing_user_login') {
          toast.error(t('customerManagement.errorUsernameExists'));
        } else if (wpCode === 'existing_user_email') {
          toast.error(t('customerManagement.errorEmailExists'));
        } else {
          toast.error(res.error);
        }
      } else {
        toast.success(t('customerManagement.createSuccess'));
        onCreated();
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const field = (
    key: keyof CreateForm,
    label: string,
    type = 'text',
    required = false,
    placeholder = ''
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
        placeholder={placeholder}
        className={`input w-full ${errors[key] ? 'input-error' : ''}`}
        disabled={loading}
      />
      {errors[key] && <p className="text-xs text-red-500 mt-1">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{t('customerManagement.quickRegister')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form id="create-customer-form" onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {field('first_name', t('customerManagement.firstName'), 'text', false, t('customerManagement.firstNamePlaceholder'))}
            {field('last_name', t('customerManagement.lastName'), 'text', false, t('customerManagement.lastNamePlaceholder'))}
          </div>
          {field('username', t('customerManagement.username'), 'text', true, 'e.g. john_doe')}
          {field('email', t('customerManagement.email'), 'email', true, 'john@example.com')}
          {field('password', t('customerManagement.initialPassword'), 'password', true)}
          {field('phone', t('customerManagement.phone'), 'tel', false, '+64 21 xxx xxxx')}
        </form>

        <div className="flex justify-end gap-3 px-6 pb-6 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="create-customer-form"
            onClick={handleSubmit as unknown as React.MouseEventHandler}
            className="btn btn-primary gap-2"
            disabled={loading}
          >
            {loading ? <Loader className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t('customerManagement.createAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── 客户详情侧面板 ───────────────────────────────────────────────────────────

interface DetailPanelProps {
  customerId: number;
  onClose: () => void;
  onDeleted: () => void;
}

const CustomerDetailPanel: React.FC<DetailPanelProps> = ({ customerId, onClose, onDeleted }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.wpCustomers.getDetail(customerId, { page, limit: 10 });
      if (res.data) {
        setDetail(res.data as unknown as CustomerDetail);
      }
    } finally {
      setLoading(false);
    }
  }, [customerId, page]);

  useEffect(() => {
    load();
  }, [load]);

  const user = detail?.user;
  const bookings = detail?.bookings || [];
  const pg = detail?.pagination;

  const handleDeleteCustomer = async () => {
    if (!user) return;
    setDeleteLoading(true);
    try {
      const res = await api.wpCustomers.delete(user.id);
      if (res.error) {
        if (res.status === 404) {
          toast.error('客户已不存在');
          return;
        }
        if (res.status === 503) {
          toast.error('WordPress 删除服务未就绪，请检查 WP API 应用密码配置');
          return;
        }
        if (res.status === 409) {
          toast.error(res.error || 'WordPress 拒绝删除该客户');
          return;
        }
        toast.error(res.error || t('errors.serverError'));
        return;
      }

      const detachedCount =
        Number((res.data as { detached_bookings_count?: number } | undefined)?.detached_bookings_count) || 0;
      toast.success(`客户删除成功，已解除 ${detachedCount} 条预订关联`);
      onDeleted();
      onClose();
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg h-full shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">{t('customerManagement.customerDetail')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && !detail ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : user ? (
          <div className="p-6 space-y-6">
            {/* 基本信息 */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-lg font-bold">
                  {(user.name || user.username).charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{user.name || user.username}</p>
                  <p className="text-xs text-gray-500">@{user.username}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="h-4 w-4 text-gray-400" />
                {user.email}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400" />
                {t('customerManagement.registeredOn')} {user.registered_date ? format(new Date(user.registered_date), 'yyyy-MM-dd') : '—'}
              </div>
              <div className="text-sm text-gray-600">
                {t('customerManagement.totalBookingsPrefix')} <span className="font-semibold text-primary-600">{user.booking_count}</span> {t('customerManagement.totalBookingsSuffix')}
              </div>
            </div>

            <div className="border border-red-200 bg-red-50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-red-700">Danger Zone</p>
              <p className="text-sm text-red-700">
                删除后将无法恢复，系统会保留历史预约快照并自动解除客户关联。
              </p>
              {!showDeleteConfirm ? (
                <button
                  type="button"
                  className="btn btn-danger gap-2"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleteLoading}
                >
                  <Trash2 className="h-4 w-4" />
                  删除客户
                </button>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-red-800">
                    确认删除：<span className="font-semibold">{user.name || user.username}</span>（{user.email}）
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn btn-danger gap-2"
                      onClick={handleDeleteCustomer}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      确认删除
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleteLoading}
                    >
                      取消
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 预订历史 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('customerManagement.bookingHistory')}</h3>
              {bookings.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">{t('customerManagement.noBookingHistory')}</p>
              ) : (
                <div className="space-y-3">
                  {bookings.map((b) => {
                    const statusInfo = STATUS_LABEL_KEYS[b.status] || { key: 'customerManagement.statusUnknown', className: 'badge-default' };
                    return (
                      <div key={b.id} className="border rounded-lg p-3 text-sm space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">
                            {b.court_name} · {b.location_name}
                          </span>
                          <span className={`badge ${statusInfo.className}`}>
                            {statusInfo.key === 'customerManagement.statusUnknown'
                              ? t(statusInfo.key, { status: b.status })
                              : t(statusInfo.key)}
                          </span>
                        </div>
                        <div className="text-gray-500">
                          {b.date} {b.start_time}–{b.end_time}
                        </div>
                        {b.total_price > 0 && (
                          <div className="text-gray-700 font-medium">
                            NZD {Number(b.total_price).toFixed(2)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {pg && pg.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                    className="btn btn-ghost btn-sm"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-gray-500">
                    {page} / {pg.total_pages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pg.total_pages, p + 1))}
                    disabled={page >= pg.total_pages || loading}
                    className="btn btn-ghost btn-sm"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-400">{t('customerManagement.loadFailed')}</div>
        )}
      </div>
    </div>
  );
};

// ─── 主页面 ───────────────────────────────────────────────────────────────────

const CustomerManagement: React.FC = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<WpCustomer[]>([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 20, total: 0, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.wpCustomers.getList({ search, page, per_page: 20 });
      if (res.data) {
        const d = res.data as unknown as { users: WpCustomer[]; pagination: typeof pagination };
        setCustomers(d.users || []);
        if (d.pagination) setPagination(d.pagination);
      } else if (res.error) {
        toast.error(res.error);
      }
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="h-7 w-7 text-primary-600" />
            {t('common.customers')}
          </h1>
          <p className="text-gray-500 mt-1">{t('customerManagement.pageDescription')}</p>
        </div>
        <button
          className="btn btn-primary gap-2"
          onClick={() => setShowCreate(true)}
        >
          <Plus className="h-4 w-4" />
          {t('customerManagement.quickRegister')}
        </button>
      </div>

      {/* 搜索框 */}
      <div className="card card-comfortable">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('customerManagement.searchPlaceholder')}
              className="input pl-10 w-full"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {t('common.search')}
          </button>
          {search && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </form>
      </div>

      {/* 客户列表 */}
      <div className="card overflow-hidden">
        {loading && customers.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {search
                ? t('customerManagement.noResultsWithSearch', { search })
                : t('customerManagement.noCustomerData')}
            </p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left">
                  <th className="px-4 py-3 font-medium text-gray-600">{t('customerManagement.customer')}</th>
                  <th className="px-4 py-3 font-medium text-gray-600">{t('customerManagement.email')}</th>
                  <th className="px-4 py-3 font-medium text-gray-600">{t('customerManagement.registeredAt')}</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-center">{t('customerManagement.bookingCount')}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {customers.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedId(c.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {(c.name || c.username).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{c.name || c.username}</p>
                          <p className="text-xs text-gray-400">@{c.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        {c.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {c.registered_date
                        ? format(new Date(c.registered_date), 'yyyy-MM-dd')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center h-6 min-w-[1.5rem] px-2 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                        {c.booking_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="btn btn-ghost btn-sm gap-1"
                        onClick={(e) => { e.stopPropagation(); setSelectedId(c.id); }}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {t('common.viewDetails')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 分页 */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <p className="text-sm text-gray-500">
                  {t('customerManagement.totalCustomers', { count: pagination.total })}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                    className="btn btn-ghost btn-sm"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-600">
                    {page} / {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                    disabled={page >= pagination.total_pages || loading}
                    className="btn btn-ghost btn-sm"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 快速注册 Dialog */}
      {showCreate && (
        <CreateCustomerDialog
          onClose={() => setShowCreate(false)}
          onCreated={load}
        />
      )}

      {/* 客户详情面板 */}
      {selectedId !== null && (
        <CustomerDetailPanel
          customerId={selectedId}
          onClose={() => setSelectedId(null)}
          onDeleted={load}
        />
      )}
    </div>
  );
};

export default CustomerManagement;
